// file: client/src/offline/SyncConflictsPage.jsx
/**
 * MODULE 09 — « Synchronisation — à vérifier » (plan §8.4). Owner/manager
 * review queue: each item shows the offline fact, the rule applied, and the
 * one-click resolution. Nothing here blocks the POS.
 */
import * as React from "react";
import apiClient from "@/services/api.client.js";
import {
  Button,
  Icon,
  Badge,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  EmptyState,
  ErrorState,
  Skeleton,
} from "@/components/ui/primitives";

const RULE_META = {
  SYNC_DECOUVERT: {
    label: "Découvert de stock",
    tone: "warning",
    hint: "Vente hors ligne au-delà du stock serveur — la vente est enregistrée, une correction forcée trace l'écart. Compter le rayon puis résoudre.",
  },
  DOUBLE_ENCAISSEMENT: {
    label: "Double encaissement",
    tone: "danger",
    hint: "Le même crédit a été encaissé sur deux terminaux — vérifier quel tiroir détient l'excédent.",
  },
  CHIFA_TR_REVIEW: {
    label: "CHIFA — écart de calcul",
    tone: "danger",
    hint: "La part CNAS recalculée dépasse la tolérance — la vente est EN ATTENTE (non appliquée). Régler le TR puis rejouer depuis le terminal.",
  },
  VENTE_HORS_SESSION: {
    label: "Session de caisse",
    tone: "warning",
    hint: "Opération de caisse rejouée sur une session déjà clôturée côté serveur.",
  },
  CORRECTION_OFFLINE: {
    label: "Correction hors ligne",
    tone: "info",
    hint: "Correction de stock saisie hors ligne (PIN local) — appliquée, à contre-vérifier.",
  },
  CHAIN_BROKEN: {
    label: "Intégrité de la file",
    tone: "danger",
    hint: "La chaîne du terminal n'a pas pu être vérifiée — synchronisation mise en quarantaine. Contacter le support.",
  },
  STOCK_BLOQUE_OU_RESERVE: {
    label: "Stock bloqué ou réservé",
    tone: "danger",
    hint: "La vente hors ligne porte sur des unités bloquées ou réservées par une fenêtre de caisse — elle est EN ATTENTE (non appliquée, stock inchangé). Vérifier le lot : lever le blocage ou attendre la fenêtre, puis ressaisir la vente au comptoir si les unités sont bien sorties.",
  },
  AUTRE: { label: "À vérifier", tone: "neutral", hint: "" },
};

const fmtDT = (iso) =>
  iso ? new Date(iso).toLocaleString("fr-DZ", { dateStyle: "short", timeStyle: "short" }) : "—";

export default function SyncConflictsPage() {
  const [rows, setRows] = React.useState(null);
  const [error, setError] = React.useState(null);
  const [busy, setBusy] = React.useState(null);
  const [tab, setTab] = React.useState("OUVERT");

  const load = React.useCallback(async () => {
    setError(null);
    try {
      const res = await apiClient.get("/sync/conflicts", { params: { statut: tab } });
      setRows(res.data ?? []);
    } catch (e) {
      setError(e?.message ?? "Chargement impossible");
      setRows([]);
    }
  }, [tab]);

  React.useEffect(() => {
    setRows(null);
    load();
  }, [load]);

  const resolve = async (id, ignore) => {
    setBusy(id);
    try {
      await apiClient.post(`/sync/conflicts/${id}/resolve`, {
        ignore,
        resolution: ignore ? "Ignoré après vérification" : "Vérifié et résolu",
      });
      await load();
    } catch (e) {
      setError(e?.message ?? "Action impossible");
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="p-6">
      <header className="mb-4 flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-xl font-bold text-text-strong">
            Synchronisation — à vérifier
          </h1>
          <p className="text-xs text-text-muted mt-0.5">
            Faits enregistrés hors ligne nécessitant un regard humain — la
            caisse n'est jamais bloquée par ces éléments.
          </p>
        </div>
        <div className="flex items-center gap-1">
          {["OUVERT", "RESOLU", "IGNORE"].map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => setTab(s)}
              className={`h-8 px-3 rounded-md text-xs font-semibold border transition-colors ${
                tab === s
                  ? "border-accent bg-accent-soft text-accent"
                  : "border-border-default bg-surface text-text-default hover:bg-surface-hover"
              }`}
            >
              {s === "OUVERT" ? "À traiter" : s === "RESOLU" ? "Résolus" : "Ignorés"}
            </button>
          ))}
        </div>
      </header>

      {rows === null ? (
        <div className="space-y-2">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} w="100%" h={52} />
          ))}
        </div>
      ) : error ? (
        <ErrorState title="Chargement impossible" description={error} retry={load} />
      ) : rows.length === 0 ? (
        <EmptyState
          icon="check-circle"
          title={tab === "OUVERT" ? "Rien à vérifier" : "Aucun élément"}
          description={
            tab === "OUVERT"
              ? "Toutes les synchronisations hors ligne sont réconciliées."
              : "Aucun élément dans cet état."
          }
        />
      ) : (
        <Table>
          <Thead>
            <Tr>
              <Th>Règle</Th>
              <Th>Opération</Th>
              <Th>Terminal</Th>
              <Th>Quand</Th>
              <Th>Détail</Th>
              {tab === "OUVERT" && <Th align="right">Actions</Th>}
            </Tr>
          </Thead>
          <Tbody>
            {rows.map((c) => {
              const meta = RULE_META[c.rule] ?? RULE_META.AUTRE;
              return (
                <Tr key={c.id}>
                  <Td>
                    <Badge tone={meta.tone} size="sm">
                      {meta.label}
                    </Badge>
                  </Td>
                  <Td>
                    <div className="text-xs font-medium text-text-strong">
                      {c.op?.op_type ?? "—"}
                      {c.op?.numero_provisoire && (
                        <span className="font-mono text-text-muted">
                          {" "}
                          · {c.op.numero_provisoire}
                        </span>
                      )}
                    </div>
                    <div className="text-2xs text-text-muted">
                      {c.op?.user ? `${c.op.user.prenom ?? ""} ${c.op.user.nom ?? ""}` : ""}
                    </div>
                  </Td>
                  <Td className="text-xs">{c.device?.label ?? "—"}</Td>
                  <Td className="text-xs font-mono">{fmtDT(c.op?.offline_ts ?? c.created_at)}</Td>
                  <Td>
                    <div className="text-2xs text-text-muted max-w-[360px]">
                      {meta.hint}
                    </div>
                    {c.evidence?.lignes_decouvert && (
                      <div className="text-2xs font-mono text-warning mt-0.5">
                        {c.evidence.lignes_decouvert
                          .map((l) => `manque ${l.manque}`)
                          .join(" · ")}
                      </div>
                    )}
                  </Td>
                  {tab === "OUVERT" && (
                    <Td align="right">
                      <div className="inline-flex items-center gap-1">
                        <Button
                          variant="primary"
                          size="xs"
                          icon="check"
                          loading={busy === c.id}
                          onClick={() => resolve(c.id, false)}
                        >
                          Résoudre
                        </Button>
                        <Button
                          variant="ghost"
                          size="xs"
                          loading={busy === c.id}
                          onClick={() => resolve(c.id, true)}
                        >
                          Ignorer
                        </Button>
                      </div>
                    </Td>
                  )}
                </Tr>
              );
            })}
          </Tbody>
        </Table>
      )}

      <p className="mt-4 text-2xs text-text-muted flex items-center gap-1.5">
        <Icon name="info" size={12} />
        Les règles automatiques (plan Module 09 §6) ont déjà enregistré les
        faits — ces éléments tracent ce qu'un responsable doit contre-vérifier.
      </p>
    </div>
  );
}
